/**
 * A block of text displaying a formatted description and comma-separated list
 * of authors (without an oxford comma)
 */

import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {Body} from "@khanacademy/wonder-blocks-typography";
import {RequestInfo} from "@khan/shared-core";
import {mediaQueries} from "@khan/shared-styles";

import type {Author} from "#content-library/types/types.ts";

import AuthorList from "./author-list.tsx";

type Props = {
    authorList: Array<Author>;
    description?: string;
    hideDescriptionOnMobile?: boolean;
};

class ContentDescription extends React.Component<Props> {
    static defaultProps: any = {
        authorList: [],
    };

    render(): React.ReactNode {
        const {authorList} = this.props;
        let description = this.props.description;

        if (!description && !authorList.length) {
            return null;
        }

        // We should add a period unless the description ends with a
        // punctuation symbol (whitespace ignored)
        // Some languages use different symbols to end the sentence.
        // These were added to the regex based on
        // https://en.wikipedia.org/w/index.php?title=Full_stop#Full_stops_in_other_scripts
        // NOTE(danielhollas): According to wiki, Armenian uses a special symbol ։
        // but I noticed that armenian translators use normal colon as well,
        // so I added it to the list.
        const shouldAddPeriod =
            description && !/[.?!"'։:。۔।។](?:\s|&nbsp;)*$/.test(description);

        if (description && shouldAddPeriod) {
            description += t({
                context: "Period at the end of the sentence.",
                message: ".",
            });
        }

        // TODO(audrey): integrate TeX with descriptions
        if (description != null) {
            description = description.replace("\\$", "$");
        }

        return (
            <div
                className={css(
                    styles.description,
                    this.props.hideDescriptionOnMobile && styles.hideMobile,
                    this.props.hideDescriptionOnMobile &&
                        RequestInfo.isTablet &&
                        styles.hideTablet,
                )}
            >
                <Body>
                    {/* @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'. */}
                    <span dangerouslySetInnerHTML={{__html: description}} />{" "}
                    <AuthorList authorList={authorList} />
                </Body>
            </div>
        );
    }
}

const styles = StyleSheet.create({
    description: {
        textAlign: "left",
    },

    hideMobile: {
        [mediaQueries.smOrSmaller]: {
            display: "none",
        },
    },
    hideTablet: {
        [mediaQueries.lg]: {
            display: "none",
        },
        [mediaQueries.md]: {
            display: "none",
        },
    },
});

export default ContentDescription;
